/**
 * Browser-side KIT calls (leads, newsletter, booking).
 *
 * These run from the visitor's browser with the public company key, so the
 * site's exact domain must be registered under Allowed Domains in the KIT
 * admin panel — the API rejects browser requests from any other origin.
 */

import { referralFields } from './referral';
import { devProxyPath } from './dev-kit';

export const API_BASE = process.env.NEXT_PUBLIC_KIT_API_URL || 'https://api.kitagency.co';
export const COMPANY_ID = process.env.NEXT_PUBLIC_KIT_COMPANY_ID || 'kit-agency';
const COMPANY_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';

/**
 * Whether this browser may call KIT directly. Only kitagency.co is registered
 * with KIT, so on localhost or a preview domain every direct call is blocked
 * (CORS) and logs an error. There, reads go through the site's own routes and
 * tracking is simply skipped: test visits should not count anyway.
 */
export function browserCanReachKit(): boolean {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h === 'kitagency.co' || h.endsWith('.kitagency.co');
}

function headers() {
  return {
    'Content-Type': 'application/json',
    'x-company-key': COMPANY_KEY,
  };
}

export async function submitLead(payload: {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  /** First-touch attribution, stored natively on the lead (see lib/attribution.ts). */
  landingPage?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}) {
  const res = await fetch(devProxyPath('leads') ?? `${API_BASE}/api/${COMPANY_ID}/leads`, {
    method: 'POST',
    headers: headers(),
    // A confirmed partner code (lib/referral.ts), if this visitor arrived on a ?ref= link.
    body: JSON.stringify({ ...payload, ...referralFields() }),
  });
  if (!res.ok) throw new Error(`Lead submission failed (${res.status})`);
  return res.json().catch(() => ({}));
}

export async function subscribeNewsletter(email: string) {
  const res = await fetch(`${API_BASE}/api/${COMPANY_ID}/newsletter/subscribe`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`Subscription failed (${res.status})`);
  return res.json().catch(() => ({}));
}

export type BookableService = {
  id: string;
  name: string;
  description?: string;
  durationMinutes?: number;
  price?: number;
  currency?: string;
};

export type Availability = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

export async function getBookableServices(): Promise<BookableService[]> {
  const res = browserCanReachKit()
    ? await fetch(`${API_BASE}/api/${COMPANY_ID}/booking/services`, { headers: headers() })
    : await fetch('/api/booking?kind=services');
  if (!res.ok) throw new Error(`Could not load services (${res.status})`);
  const data = await res.json();
  return data?.services ?? [];
}

export async function getAvailability(): Promise<Availability[]> {
  const res = browserCanReachKit()
    ? await fetch(`${API_BASE}/api/${COMPANY_ID}/booking/availability`, { headers: headers() })
    : await fetch('/api/booking?kind=availability');
  if (!res.ok) throw new Error(`Could not load availability (${res.status})`);
  const data = await res.json();
  return data?.availability ?? [];
}

export async function requestAppointment(payload: {
  bookableServiceId: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  requestedAt: string;
  notes?: string;
}) {
  const res = await fetch(`${API_BASE}/api/${COMPANY_ID}/booking/appointments`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Booking failed (${res.status})`);
  return res.json().catch(() => ({}));
}
